import type { JSONContent } from '@tiptap/core';

const updateText = (node: JSONContent, newText: string) => {
  const allMarks = node.content
    ?.filter((contentItem) => contentItem.type === 'text')
    .reduce((acc, contentItem) => {
      if (contentItem.marks) {
        acc.push(...contentItem.marks);
        return acc;
      }
      return acc;
    }, []);

  const marksMap = allMarks?.reduce(
    (acc, mark) => {
      const existingMark = acc[mark.type];
      if (existingMark?.attrs) {
        acc[mark.type] = {
          attrs: {
            ...existingMark.attrs,
            ...mark.attrs,
          },
          type: mark.type,
        };
        return acc;
      }
      acc[mark.type] = mark;
      return acc;
    },
    {} as Record<string, any>,
  );

  const marks = Object.values(marksMap || {});

  return {
    ...node,
    content: [
      {
        type: 'text',
        text: newText,
        marks,
      },
    ],
  };
};

export const formatTimeAgo = (postTime: Date): string => {
  const now = new Date();
  const diffInMs = now.getTime() - postTime.getTime();
  const diffInSeconds = Math.floor(diffInMs / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);
  const diffInWeeks = Math.floor(diffInDays / 7);
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInDays / 365);

  if (diffInSeconds < 60) {
    return 'just now';
  }
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
  }
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
  }
  if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
  }
  if (diffInWeeks < 4) {
    return `${diffInWeeks} week${diffInWeeks === 1 ? '' : 's'} ago`;
  }
  if (diffInMonths < 12) {
    return `${diffInMonths} month${diffInMonths === 1 ? '' : 's'} ago`;
  }
  return `${diffInYears} year${diffInYears === 1 ? '' : 's'} ago`;
};

const formatPostTime = (postTimeISO: string, style: string) => {
  const postTime = new Date(postTimeISO);

  // Handle different styles
  switch (style) {
    case 'short_date':
      return postTime.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    case 'numeric_date':
      return postTime.toLocaleDateString('en-US');
    case 'iso_date':
      return postTime.toISOString().split('T')[0]; // ISO format
    case 'day_first':
      return postTime.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    case 'month_year':
      return postTime.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
      });
    case 'time_ago':
      return formatTimeAgo(postTime);
    default:
      // Fallback to a standard format if the style is unknown
      return postTime.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
      });
  }
};

const getClientNavigationLink = (href: string) => {
  // If the link starts with a protocol, return it as is
  if (href.startsWith('http')) {
    return href;
  }
  // only handle post links `/p/:slug` for now
  if (!href.startsWith('/p/')) {
    return href;
  }
  // Use a base URL to create a valid URL object
  const url = new URL(href, 'http://localhost');
  // append the _data param for Remix to load the right route
  url.searchParams.set('_data', 'routes/p/$slug');
  return `${url.pathname}${url.search}`;
};

export { updateText, formatPostTime, getClientNavigationLink };
